import { useState } from 'react';

import ReactSelect, { ClassNamesConfig, components, GroupBase, InputProps, SelectComponentsConfig, Props as SelectProps } from 'react-select';

import { cn } from '@/lib/utils';

import ArrowDownSmallIcon from '../icons/arrow-down-small';
import CloseSmallIcon from '../icons/close-small';

export function Select<Option>({ styles, components, ...props }: SelectProps<Option, false, GroupBase<Option>> & { error?: boolean }) {
  return (
    <ReactSelect
      {...props}
      classNames={SELECT_STYLES<Option>(props.error)}
      components={{
        ...SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export const SELECT_COMPONENTS: <Option>() => SelectComponentsConfig<Option, false, GroupBase<Option>> = () => ({
  DropdownIndicator: (props) =>
    props.selectProps.isDisabled ? null : (
      <components.DropdownIndicator {...props}>
        <ArrowDownSmallIcon />
      </components.DropdownIndicator>
    ),
  ClearIndicator: (props) => (
    <components.ClearIndicator {...props}>
      <CloseSmallIcon />
    </components.ClearIndicator>
  ),
});

export const SELECT_STYLES: <Option>(error?: boolean) => ClassNamesConfig<Option, false> = (error) => ({
  control: (state) =>
    cn(
      'p-[8px] border-grey-3 focus:ring-ring rounded border-2 bg-white px-3 text-sm ring-offset-white focus:outline-none flex items-center cursor-pointer',
      state.isFocused && 'border-default-blue',
      error && '!border-default-red',
      state.isDisabled && 'cursor-not-allowed bg-grey-1 text-default-text hover:bg-grey-1 disabled:pointer-events-auto disabled:opacity-100',
    ),
  menu: () => 'max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-default-text shadow-md mt-1 !pointer-events-auto',
  menuPortal: () => '!z-50 !pointer-events-auto',
  menuList: () => 'p-1',
  option: (state) =>
    cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 !text-sm outline-none',
      state.isFocused && 'bg-grey-1',
      state.isDisabled && 'cursor-default text-secondary-text pointer-events-none',
      state.isSelected && 'bg-grey-1',
    ),
  placeholder: () => 'text-grey-5 leading-none truncate',
  noOptionsMessage: () => 'text-sm text-grey-5 p-2',
  loadingMessage: () => 'text-sm text-grey-5 p-2',
});

export function DoubleClickInput<T>({
  setIsMenuOpen,
  ...props
}: InputProps<T, false> & {
  setIsMenuOpen: (isMenuOpen: boolean) => void;
}) {
  const [inputMode, setInputMode] = useState<'none' | 'text'>('none');

  return (
    <components.Input
      {...props}
      autoFocus={false}
      inputMode={inputMode}
      onFocus={() => {
        setInputMode('text');
        setIsMenuOpen(true);
      }}
      onBlur={() => {
        setIsMenuOpen(false);
        setInputMode('none');
      }}
    />
  );
}
